import { Form, FormProps } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { Grid2 as Grid, Stack } from "@mui/material";
import { z } from "zod/v4";
import { useState, useRef } from "react";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { NaturalezaReceptorEnum } from "@/shared/types/contribuyente";
import DEArticuloSearch, { DetalleDE } from "../documento-electronico-form/de-articulo-search";
import DEArticuloList from "../documento-electronico-form/de-articulo-list";
import DECondicionPagoFields from "../documento-electronico-form/de-condicion-pago-fields";
import DocumentoAsociadoFields from "../documento-electronico-form/documento-asociado-fields";
import AutofacturaGeneralInfoFields from "./autofactura-general-info-fields";
import { autofacturaFormSchema } from "../../schemas";
import type { EmisorData } from "../../utils/de-types";
import { Cliente } from "@/shared/types/cliente";

export { autofacturaFormSchema };

interface AutofacturaFormProps extends FormProps<z.input<typeof autofacturaFormSchema>> {
    emisor: EmisorData | null;
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
    clienteIncompleto?: boolean;
}

export function AutofacturaForm({
    form,
    onClienteChange,
    cliente,
    clienteIncompleto,
    ...props
}: AutofacturaFormProps) {
    const openModal = useOpenModal();
    const detalleFormRef = useRef<any>(null);
    const [showClienteError, setShowClienteError] = useState(false);

    const handleFormSubmit = () => {
        if (!cliente) {
            setShowClienteError(true);
            return false;
        }
        setShowClienteError(false);
        return true;
    };

    const canFormSubmit = () => {
        if (!cliente) {
            return false;
        }
        if (cliente.naturaleza === NaturalezaReceptorEnum.Contribuyente) {
            return false;
        }
        return true;
    };

    const handleReset = () => {
        form.reset();
        onClienteChange?.(null); // Reset 
        form.setFieldValue("vendedorId", "");
        form.setFieldValue("tipoTransaccion", "2");
        form.setFieldValue("condicionOperacion", "contado");
        form.setFieldValue("pagos", [{ tipoPago: "efectivo", monto: 0 }]);
        form.setFieldValue("tipoCredito", "plazo");
        form.setFieldValue("plazoDias", "30");
        form.setFieldValue("tipoDocumentoAsociado", "3");
        form.setFieldValue("items", []);
        setShowClienteError(false);
    };

    return <Form form={form} gap={2} {...props}>
        <AutofacturaGeneralInfoFields
            detalleFormRef={detalleFormRef}
            onClienteChange={onClienteChange}
            cliente={cliente}
            clienteError={showClienteError}
            clienteIncompleto={clienteIncompleto}
        />
        <DocumentoAsociadoFields cdcLoaded={false} setCdcLoaded={() => { }} forAutofactura={true} />
        <CollapsibleSection title="Items">
            <Grid container columns={{ xs: 1, md: 3 }} spacing={2} flex={1}>
                <Grid component={Stack} size={1}>
                    <DEArticuloSearch
                        ref={detalleFormRef}
                        forAutoFactura={true}
                        onArticuloSelect={(v: DetalleDE) => {
                            const items = form.getFieldValue("items");
                            const index = items.findIndex(i => i.descripcion === v.descripcion)
                            if (index != -1) {
                                const previousValue = items[index];
                                const newValue = { ...previousValue, cantidad: previousValue.cantidad + v.cantidad }
                                form.replaceFieldValue("items", index, newValue);
                            } else {
                                form.pushFieldValue("items", {
                                    codigo: v.codigo,
                                    descripcion: v.descripcion,
                                    cantidad: v.cantidad,
                                    precio: v.precio,
                                    iva: v.iva,
                                    unidadMedida: v.unidadMedida,
                                });
                            }
                        }} />
                </Grid>

                <Grid component={Stack} size={2} gap={2}>
                    <form.Field name="items" mode="array">
                        {field => <>
                            <DEArticuloList
                                items={(field.state.value || []) as any[]}
                                remove={field.removeValue}
                                forAutoFactura={true}
                            />
                        </>}
                    </form.Field>
                </Grid>
            </Grid>
        </CollapsibleSection>
        <DECondicionPagoFields forAutofactura={true} />
        <Stack direction={"row"} justifyContent={"end"} gap={2}>
            <Form.Button onClick={() => openModal("confirmReset")}>Cancelar</Form.Button>
            <Form.SubmitButton onClick={handleFormSubmit} disabled={!canFormSubmit()}>Confirmar</Form.SubmitButton>
        </Stack>

        <ConfirmDialog code="confirmReset">
            <ConfirmDialog.Content
                title="Limpiar Autofactura"
                description="¿Estás seguro que quieres limpiar la autofactura actual?"
                onConfirm={handleReset}
            />
        </ConfirmDialog>
    </Form>;
}
